import React from "react";

import DeleteIcon from "@material-ui/icons/Delete";
import SettingsIcon from "@material-ui/icons/Settings";
import { Grid, IconButton, makeStyles, Paper, Typography } from "@material-ui/core";
import { AspectRatios } from "../../../entities/AspectRatios";
import { PreviewSlide, SlideLineChar } from "./PreviewSlide";

const useStyles = makeStyles((theme) => ({
	backgroundCard: {
		width: "90%",
		marginLeft: "auto",
		marginRight: "auto",
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
		transition: "all 0.3s ease 0s",
	},
	paper: {
		width: "296px",
		backgroundSize: "100%",
		margin: "0 auto",
		wordWrap: "break-word",
		overflow: "hidden",
		"&:hover": {
			"& button": {
				opacity: 1,
				transition: "all 0.3s ease 0s",
			},
		},
	},
	fillParent: {
		width: "100%",
		height: "100%",
	},
	iconButton: {
		opacity: 0.4,
		transition: "all 0.3s ease 0s",
		padding: "7px",
		"&:hover": {
			backgroundColor: "rgba(0, 0, 0, 0.5)",
		},
	},
	deleteSlideButton: {
		right: "10px",
		bottom: "10px",
		position: "absolute",
		display: "flex",
	},
	gridItem: {
		padding: "10px",
		whiteSpace: "initial",
		userSelect: "none",
		overflowY: "hidden",
	},
	imageTitle: {
		textAlign: "center",
		marginTop: 3,
	},
	slidePreviewText4x3Small: {
		fontSize: 8,
		padding: 16,
	},
	slidePreviewText16x9Small: {
		fontSize: 5.5,
		padding: 18,
	},
	slidePreviewText4x3Medium: {
		fontSize: 13.8,
		padding: 24,
	},
	slidePreviewText16x9Medium: {
		fontSize: 12,
		padding: 19,
	},
	slidePreviewText4x3Large: {
		fontSize: 22,
		padding: 23,
	},
	slidePreviewText16x9Large: {
		fontSize: 20,
		padding: 12,
	},
}));

interface Props {
	key: number;
	index: number;
	slideExporterAspectRatio: AspectRatios;
	previewSlide: PreviewSlide;
	onRemovePreviewSlide: (slide: PreviewSlide) => void;
	getImageThumbnail: () => string;
	getFontColor: () => string;
	onSettingsDialogToggle: () => void;
}

export function DisplaySlide(props: Props) {
	let { key, index, slideExporterAspectRatio, previewSlide } = props;
	let { onRemovePreviewSlide, getImageThumbnail, getFontColor, onSettingsDialogToggle } = props;
	const classes = useStyles();
	const addSurroundingTag = (html: string, tagName: string, style?: string) => {
		return `<${tagName}${style !== undefined ? ` style="${style}"` : ""}>${html}</${tagName}>`;
	};

	const generateLineCharacterHtml = (lineChars: SlideLineChar[]) => {
		let lineHtml = "";
		lineChars.forEach((lineChar) => {
			let characterHtml = lineChar.char;
			if (characterHtml === "\n") {
				lineHtml += "<br/>";
				return;
			}
			if (lineChar.isBold) {
				characterHtml = addSurroundingTag(characterHtml, "b");
			}
			if (lineChar.isItalics) {
				characterHtml = addSurroundingTag(characterHtml, "i");
			}
			if (lineChar.isUnderlined) {
				characterHtml = addSurroundingTag(characterHtml, "u");
			}
			if (lineChar.backgroundColor !== undefined) {
				characterHtml = addSurroundingTag(characterHtml, "span", `background-color:${lineChar.backgroundColor};`);
			}
			if (lineChar.fontFamily !== undefined) {
				characterHtml = addSurroundingTag(characterHtml, "span", `font-family:${lineChar.fontFamily};`);
			}
			if (lineChar.fontSize !== undefined) {
				characterHtml = addSurroundingTag(characterHtml, "span", `font-size:${lineChar.fontSize};`);
			}

			lineHtml += characterHtml;
		});

		return lineHtml;
	};

	const generateSlideLineHtml = () => {
		let blockType = "p";
		let containsList = false;
		let listType = null;
		let alignment = "";
		let slideLinesHtml = previewSlide.lines.map((slideLine) => {
			let lineHtml = generateLineCharacterHtml(slideLine.lineChars);

			if (slideLine.blockTag !== undefined) {
				blockType = slideLine.blockTag;
			}

			if (slideLine.textAlignment) {
				alignment = slideLine.textAlignment;
			}

			if (slideLine.isUnorderedList || slideLine.isOrderedList) {
				lineHtml = `<li>${lineHtml}</li>`;
				containsList = true;
				listType = slideLine.isOrderedList ? "ol" : "ul";
				blockType = "span";
			}

			if (slideLine.isBlockQuote) {
				lineHtml = `<blockquote style="font-style: italic">${lineHtml}</blockquote>`;
				blockType = "span";
			}

			return lineHtml;
		});

		if (containsList) {
			let listLineIndexes: number[] = [];
			slideLinesHtml.forEach((slideLineHtml, index) => {
				let slideIsListBullet = slideLineHtml.indexOf("<li>") !== -1;
				if (slideIsListBullet) {
					listLineIndexes.push(index);
				}
			});

			slideLinesHtml.splice(listLineIndexes[0], 0, listType === "ol" ? `<ol>` : `<ul>`);
			slideLinesHtml.splice(listLineIndexes[listLineIndexes.length - 1] + 2, 0, listType === "ol" ? "</ol>" : "</ul>");
		}

		let blockStyles = `color:${getFontColor()};`;
		if (alignment) {
			blockStyles += `text-align:${alignment};`;
		}
		let result = addSurroundingTag(slideLinesHtml.join(""), blockType, blockStyles);
		return result;
	};

	return (
		<li className={classes.backgroundCard} key={key}>
			<div title={"Slide " + (index + 1)}>
				<Paper
					elevation={2}
					className={classes.paper}
					style={{
						position: "relative",
						backgroundImage: `url(${getImageThumbnail()})`,
						height: slideExporterAspectRatio === "4x3" ? "223px" : "166px",
					}}
				>
					<div className={classes.deleteSlideButton}>
						<IconButton
							className={classes.iconButton}
							onClick={() => onSettingsDialogToggle()}
							style={{ color: getFontColor() }}
							color="inherit"
						>
							<SettingsIcon fontSize={"small"} />
						</IconButton>
						<IconButton
							className={classes.iconButton}
							onClick={() => onRemovePreviewSlide(previewSlide)}
							style={{ color: getFontColor() }}
							color="inherit"
						>
							<DeleteIcon fontSize={"small"} />
						</IconButton>
					</div>
					<Grid container justifyContent="center" direction="column" className={classes.fillParent} onDoubleClick={() => onSettingsDialogToggle()}>
						<Grid item className={classes.gridItem}>
							{<div dangerouslySetInnerHTML={{ __html: generateSlideLineHtml() }} key={index} />}
						</Grid>
					</Grid>
				</Paper>
				<Typography className={classes.imageTitle}> Slide {index + 1}</Typography>
			</div>
		</li>
	);
}